import Enumerable from "linq";
import {
  Document,
  DocumentAttributes,
  Section,
  ItemSet,
  ReferenceFileInformation,
  ExternalScriptParameter,
  EditPhaseKinds,
  ObjectId,
  IDocumentAlert,
  IBookmark,
} from "../../../domain/document";
import { PatientProfileInformation } from "../../../domain/patient";
import {
  IItem,
  IDrawable,
  IDrawableItem,
  instanceOfIDrawable,
  instanceOfIDrawableItem,
  AbstractValuedItem,
  instanceOfIRelatedExternalScriptExecutable,
  instanceOfIPasteRequestable,
  instanceOfIDateLinkRequestable,
  instanceOfIIsValueUpdatedSpecifiable,
  instanceOfIQuotable,
  instanceOfINaturalTextSpecifiable,
} from "../../../domain/document/items";
import { FormatReference } from "../../../domain/format";
import { HisInformation } from "../../../domain/his";
import { Comparator } from "../../../domain/system";
import {
  TimeMeasure,
  BinaryFinder,
} from "../../../utilities";
import {
  IRestriction,
} from "../../system";
import {
  ExternalScriptRunner,
  EditingDocumentMerger,
  HisInformationApplicator,
  ObjectPositionSlider,
} from "./helpers";
import { DateLinkExecutor, IDocumentEditable, IDocumentItemUpdateOptions, PasteExecutor } from ".";
import { SourceApplicator } from "./SourceApplicator";
import { DesignatedDoctorInformation } from "../../../domain/user";
import store from "../../../store";
import { LoggerFactory } from "../../../utilities/logger";
import { IDrawableItemEditable } from "./IDrawableItemEditable";
import { UpdateObjectCountExceedError } from "../../../domain/system/error";
import { InstitutionItem } from "../../../domain/institution";
import { IConfiguration } from "../../../domain/configuration";
import { EmptyValidator } from "../../../domain/document/validators";
import { InitialAlert } from "../../../domain/document/alerts";

export interface IRegistrationRestrictions {
  asNormally?: IRestriction;
  asTemporary?: IRestriction;
  andPrinting?: IRestriction;
  asTemporaryAndPrinting?: IRestriction;
  asTemporaryAndPrintingWithWatermark?: IRestriction;
  andPrintingWithDialog?: IRestriction;
}

export interface IToolRestrictions {
  // Docu2Web本体の文書編集以外での利用時に非表示設定可能なタブを追加する
  createdReport?: IRestriction;
  claio?: IRestriction;
  claioSchema?: IRestriction;
  externalImage?: IRestriction;
  ocojo?: IRestriction;
}

export interface IClipboardRestrictions {
  imageRegistration?: IRestriction;
}

export interface IRestrictions {
  validation?: IRestriction;
  registration?: IRegistrationRestrictions;
  printing?: IRestriction;
  pageList?: IRestriction;
  tools?: IToolRestrictions;
  preview?: IRestriction;
  note?: IRestriction;
  clipboard?: IClipboardRestrictions;
}

export class EditingDocument implements IDocumentEditable {
  constructor(
    source: Document,
    restrictions?: IRestrictions,
    currentSectionIndex?: number,
    lastFocusedItem?: IItem,
    lastFocusedObject?: IDrawableItemEditable,
    scale?: number,
    shouldDrawEditStyle?: boolean,
    // TODO: 見直し.本来はEditingDocumentに内包したくないが、現状癒着が激しいので許容する.
    externalScriptRunner?: ExternalScriptRunner,
    shouldInputFreeAll?: boolean,
    alerts?: IDocumentAlert[],
    shouldMarkAlerts?: boolean,
    shouldDrawTransparentAll?: boolean,
    shouldMarkQuote?: boolean,
  ) {
    this._source = source;
    this.restrictions = restrictions;
    this.currentSectionIndex = currentSectionIndex;
    this.lastFocusedItem = lastFocusedItem;
    this.lastFocusedObject = lastFocusedObject;
    this.scale = scale;
    this.shouldDrawEditStyle = (shouldDrawEditStyle !== undefined && shouldDrawEditStyle !== null)
      ? shouldDrawEditStyle
      : store.state.configuration.behavior.document.editor.contents.edit.selector.drawModeSelector.defaultShouldDrawEditStyle;
    this._externalScriptRunner = externalScriptRunner
      ? externalScriptRunner
      : new ExternalScriptRunner(this);
    this._externalScriptRunner.source = this;
    this.shouldInputFreeAll = shouldInputFreeAll;
    this.alerts = alerts;
    this.shouldMarkAlerts = shouldMarkAlerts;
    this.shouldDrawTransparentAll = shouldDrawTransparentAll;
    this.shouldMarkQuote = shouldMarkQuote;
    this._idDictionary = Enumerable.from(source.itemSets)
      .select(EditingDocument.toIdDictionary)
      .toArray();
    this._nameDictionary = Enumerable.from(source.itemSets)
    .select(EditingDocument.toNameDictionary)
    .toArray();
    this._externalScriptRunner.shouldRestrictScript = source.shouldRestrictExternalScript;
    this._externalScriptRunner.allowedScripts = source.shouldRestrictExternalScript
      ? source.allowedExternalScripts
      : null;
  }

  get alerts(): IDocumentAlert[] {
    return this._alerts;
  }

  set alerts(value: IDocumentAlert[]) {
    this._alerts = value
      ? value
      : [];
  }

  private _alerts: IDocumentAlert[];

  get shouldMarkAlerts(): boolean {
    return this._shouldMarkAlerts;
  }

  set shouldMarkAlerts(value: boolean) {
    this._shouldMarkAlerts = ((value !== undefined) && (value !== null))
      ? value
      : false;
  }

  private _shouldMarkAlerts: boolean = false;

  get shouldDrawTransparentAll(): boolean {
    return this._shouldDrawTransparentAll;
  }

  set shouldDrawTransparentAll(value: boolean) {
    this._shouldDrawTransparentAll = (value !== undefined && value !== null && value);
  }

  private _shouldDrawTransparentAll: boolean = false;

  get shouldMarkQuote(): boolean {
    return this._shouldMarkQuote;
  }

  set shouldMarkQuote(value: boolean) {
    this._shouldMarkQuote = (value !== undefined && value !== null && value);
  }

  private _shouldMarkQuote: boolean = false;

  get restrictions(): IRestrictions {
    return this._restrictions;
  }

  set restrictions(value: IRestrictions) {
    const restrinctions = this.applyDocumentRestrictions(value);
    this._restrictions = restrinctions
      ? restrinctions
      : null;
  }

  private applyDocumentRestrictions(value: IRestrictions): IRestrictions {
    const restrinctions = this.applyIsRegisterableRegisteredDocumentAsTemporary(value);
    return this.applyIsPrintableRegisteredDocumentAsTemporary(restrinctions);
  }

  private applyIsRegisterableRegisteredDocumentAsTemporary(value: IRestrictions): IRestrictions {
    if (!this.source || this.isRegisterableRegisteredDocumentAsTemporary) { return value; }
    if (!value) {
      value = {
        registration: {
          asTemporary: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
          asTemporaryAndPrinting: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
          asTemporaryAndPrintingWithWatermark: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
        }
      };
    }
    if (!value.registration) {
      value.registration = {
        asTemporary: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
        asTemporaryAndPrinting: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
        asTemporaryAndPrintingWithWatermark: { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, },
      };
    }
    if (!value.registration.asTemporary) {
      value.registration.asTemporary = { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, };
    }
    if (!value.registration.asTemporaryAndPrinting) {
      value.registration.asTemporaryAndPrinting = { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, };
    }
    if (!value.registration.asTemporaryAndPrintingWithWatermark) {
      value.registration.asTemporaryAndPrintingWithWatermark = { isDisabled: !this.isRegisterableRegisteredDocumentAsTemporary, };
    }
    value.registration.asTemporary.isDisabled = !this.isRegisterableRegisteredDocumentAsTemporary;
    value.registration.asTemporaryAndPrinting.isDisabled = !this.isRegisterableRegisteredDocumentAsTemporary;
    value.registration.asTemporaryAndPrintingWithWatermark.isDisabled = !this.isRegisterableRegisteredDocumentAsTemporary;
    return value;
  }

  private applyIsPrintableRegisteredDocumentAsTemporary(value: IRestrictions): IRestrictions {
    if (!this.source || this.isPrintableRegisteredDocumentAsTemporary) { return value; }
    if (!value) {
      value = {
        registration: {
          asTemporaryAndPrinting: { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, },
          asTemporaryAndPrintingWithWatermark: { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, },
        },
      };
    }
    if (!value.registration) {
      value.registration = {
        asTemporaryAndPrinting: { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, },
        asTemporaryAndPrintingWithWatermark: { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, },
      };
    }
    if (!value.registration.asTemporaryAndPrinting) {
      value.registration.asTemporaryAndPrinting = { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, };
    }
    if (!value.registration.asTemporaryAndPrintingWithWatermark) {
      value.registration.asTemporaryAndPrintingWithWatermark = { isDisabled: !this.isPrintableRegisteredDocumentAsTemporary, };
    }
    value.registration.asTemporaryAndPrinting.isDisabled = !this.isPrintableRegisteredDocumentAsTemporary;
    value.registration.asTemporaryAndPrintingWithWatermark.isDisabled = !this.isPrintableRegisteredDocumentAsTemporary;
    return value;
  }

  get isRegisterableRegisteredDocumentAsTemporary(): boolean {
    return !this.source || this.source.attributes.isRegisterableRegisteredDocumentAsTemporary === undefined || this.source.attributes.isRegisterableRegisteredDocumentAsTemporary;
  }

  get isPrintableRegisteredDocumentAsTemporary(): boolean {
    return !this.source || this.source.attributes.isPrintableRegisteredDocumentAsTemporary === undefined || this.source.attributes.isPrintableRegisteredDocumentAsTemporary;
  }

  get isPrintingDisabled(): boolean {
    return this.restrictions
        && this.restrictions.printing
        && this.restrictions.printing.isDisabled;
  }

  get isPageListDisabled(): boolean {
    return this.restrictions
        && this.restrictions.pageList
        && this.restrictions.pageList.isDisabled;
  }

  get isPreviewDisabled(): boolean {
    return this.restrictions
        && this.restrictions.preview
        && this.restrictions.preview.isDisabled;
  }

  get isImageRegistrationDisabled(): boolean {
    return this.restrictions
        && this.restrictions.clipboard
        && this.restrictions.clipboard.imageRegistration
        && this.restrictions.clipboard.imageRegistration.isDisabled;
  }

  private _restrictions: IRestrictions;

  private static toIdDictionary(itemSet: ItemSet): any {
    const results = {} as any;
    for (const item of itemSet.items) {
      if (item && item.id && !results[item.id]) {
        results[item.id] = item.privateId;
      }
    }
    return results;
  }

  private readonly _idDictionary: any[];  // ID逆引き辞書.(item更新ハンドリングの高速化のためのキャッシュ.)

  private static toNameDictionary(itemSet: ItemSet): any {
    const results = {} as any;
    for (const item of itemSet.items) {
      const casted = (item as AbstractValuedItem);
      if (casted && casted.name && !results[casted.name]) {
        results[casted.name] = item.privateId;
      }
    }
    return results;
  }

  private readonly _nameDictionary: any[];  // name逆引き辞書.(item更新ハンドリングの高速化のためのキャッシュ.)

  private _externalScriptRunner: ExternalScriptRunner;

  get externalScriptRunner(): ExternalScriptRunner {
    return this._externalScriptRunner;
  }

  private _scale: number = 1;

  get scale(): number {
    return this._scale;
  }

  set scale(value: number) {
    this._scale = value
      ? value
      : 1;
  }

  private _shouldDrawEditStyle: boolean = true;

  get shouldDrawEditStyle(): boolean {
    return this._shouldDrawEditStyle;
  }

  set shouldDrawEditStyle(value: boolean) {
    this._shouldDrawEditStyle = ((value !== undefined) && (value !== null))
      ? value
      : store.state.configuration.behavior.document.editor.contents.edit.selector.drawModeSelector.defaultShouldDrawEditStyle;
  }

  private _shouldInputFreeAll: boolean = false;

  /** 全オブジェクトの入力方法をフリー入力にするか */
  get shouldInputFreeAll(): boolean {
    return this._shouldInputFreeAll;
  }

  set shouldInputFreeAll(value: boolean) {
    this._shouldInputFreeAll = (value != null) ? value : false;
  }

  private _source: Document;

  get source(): Document {
    return this._source;
  }

  set source(source: Document) {
    this._source = source;
  }

  get itemSets(): ItemSet[] {
    return this.source
      ? this.source.itemSets
      : [];
  }

  get sections(): Section[] {
    return this.source
      ? this.source.sections
      : [];
  }

  get bookmarks(): IBookmark[] {
    return this.source && this.source.bookmarks
      ? this.source.bookmarks
      : [];
  }

  private _currentSectionIndex: number = 0;

  get currentSectionIndex(): number {
    return this._currentSectionIndex;
  }

  set currentSectionIndex(value: number) {
    this._currentSectionIndex = (value && (0 <= value))
      ? ((value < this.sections.length) ? value : (this.sections.length - 1))
      : 0;
    this.lastFocusedItem = null;
  }

  get currentSection(): Section {
    return (0 < this.sections.length)
      ? this.sections[this.currentSectionIndex]
      : null;
  }

  get itemSetId(): number {
    return this.currentSection
      ? this.currentSection.itemSetId
      : null;
  }

  get itemSet(): ItemSet {
    return (0 < this.itemSets.length)
      ? this.itemSets[this.itemSetId]
      : null;
  }

  get items(): IItem[] {
    return this.itemSet
      ? this.itemSet.items
      : [];
  }

  get drawableItems(): IDrawableItem[] {
    return this.getDrawableItems(this.currentSectionIndex);
  }

  private _lastFocusedItem: IItem;

  get lastFocusedItem(): IItem {
    return this._lastFocusedItem;
  }

  set lastFocusedItem(value: IItem) {
    this._lastFocusedItem = value
      ? value
      : null;
  }

  updateFocusedItem(itemSetId: number, value: IItem) {
    this._currentSectionIndex = this.getFocusTargetSectionIndex(itemSetId, value);
    this._lastFocusedItem = value;
  }

  getFocusTargetSectionIndex(itemSetId: number, item: IItem): number {
    // findIndex使いたいが、IE非対応
    for (let sectionIndex = 0; sectionIndex < this.sections.length; ++sectionIndex) {
      const section = this.sections[sectionIndex];
      if (section.itemSetId === itemSetId && Enumerable.from(section.itemPrivateIds).contains(item.privateId)) {
        return sectionIndex;
      }
    }
    return this.currentSectionIndex;
  }

  private _lastFocusedObject: IDrawableItemEditable;

  get lastFocusedObject(): IDrawableItemEditable {
    return this._lastFocusedObject;
  }

  set lastFocusedObject(value: IDrawableItemEditable) {
    this._lastFocusedObject = value
      ? value
      : null;
  }

  injectInitializer(initializer: (s: Document) => Promise<void>): void {
    this._initializer = initializer;
  }

  private _initializer: (s: Document) => Promise<void>;

  async initialize(): Promise<void> {
    await TimeMeasure.logAsync(
      "編集文書初期化",
      async () => {
        if (this._initializer) {
          await this._initializer(this.source);
          // 引用で続紙が追加されたとき、続紙内に計算式が含まれているとこの後の初期化処理で辞書と不整合になりエラーが発生する
          this.updateDictionary();
        }
        this._logger.info("編集文書アイテム初期化開始");
        for (let itemSetId = 0; itemSetId < this.itemSets.length; ++itemSetId) {
          TimeMeasure.log(
            "編集文書アイテムセット初期化",
            () => {
              const itemSet = this.itemSets[itemSetId];
              Enumerable.from(itemSet.items)
                .where(i => !!i)
                .forEach(i => {
                  const cloned = i.initialize(f => this.findItem(itemSetId, itemSet.items, f), f => this.findItems(itemSetId, itemSet.items, f));
                  if (cloned) {
                    this.updateItem(cloned, {
                      itemSetId,
                    });
                  }
                });
            },
            "(itemSets[" + itemSetId + "])",
          );
        }
        this._logger.info("編集文書アイテム初期化完了");
        await this.executeStackedScriptsAsync();
        this._logger.info("外部スクリプト実行完了");
        const configuration = store.getters.configuration as IConfiguration;
        if (configuration.behavior.document.editor.validator.mark.shouldShowEmptyAlertMarkOnEditStart) {
          // 編集開始時に空の入力必須項目にマークを表示する
          this.executeEmptyValidator();
          this.shouldMarkAlerts = true;
        }
        this._isAlreadyInitialized = true;
      },
      "(ID:" + (this.source.documentId ? this.source.documentId : "") + ",FID:" + this.source.attributes.format.id + ")",
    );
  }

  private executeEmptyValidator(): void {
    // 警告タブが自動で開いてしまうので、InitialAlertであれば開かないようにガードを追加している
    const validattor = new EmptyValidator();
    this.alerts = validattor.validate(this._source).map(e => new InitialAlert(e));
  }

  private updateDictionary(): void {
    // 本来であれば追加オブジェクトについてもケアしたほうが良さそうではあるが、追加オブジェクトは他のオブジェクトとは関連しないため、いったん処理対象にはしていない
    // 落ち着いたタイミングで検討予定
    for (let itemSetId = 0; itemSetId < this.itemSets.length; ++itemSetId) {
      if (!this._idDictionary[itemSetId]) {
        this._logger.debug("ID辞書を追加します。ItemSetId=" + itemSetId);
        this._idDictionary.splice(itemSetId, 0, EditingDocument.toIdDictionary(this.itemSets[itemSetId]));
      }
      if (!this._nameDictionary[itemSetId]) {
        this._logger.debug("名前辞書を追加します。ItemSetId=" + itemSetId);
        this._nameDictionary.splice(itemSetId, 0, EditingDocument.toNameDictionary(this.itemSets[itemSetId]));
      }
    }
  }

  private _isAlreadyInitialized: boolean = false;

  get isAlreadyInitialized(): boolean {
    return this._isAlreadyInitialized;
  }

  private createExternalScriptParameter(): ExternalScriptParameter {
    return new ExternalScriptParameter(
      this.attributes.patient,
      store.getters.user,
      this.attributes.department,
      this.attributes.orderNo,
      this.attributes.format.originalId,
      this.attributes.hisInfoGetterParameter);
  }

  async executeStackedScriptsAsyncWithResult(): Promise<boolean> {
    const param = this.createExternalScriptParameter();
    return this.externalScriptRunner.executeStackedScriptsAsyncWithResult(param);
  }

  async executeStackedScriptsAsync(): Promise<void> {
    const param = this.createExternalScriptParameter();
    await this.externalScriptRunner.executeStackedScriptsAsync(param);
  }

  async executeScriptInDocumentAsync(phase: EditPhaseKinds, shouldAddToRequestStack: boolean = false): Promise<void> {
    const param = this.createExternalScriptParameter();
    await this.externalScriptRunner.executeScriptInDocumentAsync(phase, param, shouldAddToRequestStack);
  }

  insertItemSet(itemSet: ItemSet, sections: Section[], sectionIndex: number): number {
    if (sectionIndex < 0) { throw new Error("sectionIndex is invalid!!!"); }

    // DocumentのItemSetsに追加
    itemSet.freezeItems();
    const newItemSetId = this.pushItemSet(itemSet);

    // Sectionにキー情報を格納(追加の準備をする)
    const newSections: Section[] = [];
    for (const section of sections) {
      const cloned = section.clone();
      cloned.itemSetId = newItemSetId;
      newSections.push(cloned);
    }

    // DocumentのSectionsに追加
    for (let i = 0; i < newSections.length; i++) {
      const newSection = newSections[i];
      this.sections.splice(sectionIndex + i, 0, newSection);
    }

    this.currentSectionIndex = sectionIndex;
    return newItemSetId;
  }

  pushItemSet(itemSet: ItemSet): number {
    const newItemSetId = this.itemSets.length;
    this.itemSets.splice(newItemSetId , 0, itemSet.clone());
    this._idDictionary.splice(newItemSetId, 0, EditingDocument.toIdDictionary(itemSet));
    this._nameDictionary.splice(newItemSetId, 0, EditingDocument.toNameDictionary(itemSet));
    return newItemSetId;
  }

  removeSection(section: Section): void {
    const itemSetId = section.itemSetId;

    // DocumentのSectionsから削除
    const sectionIndex = this.getSectionIndex(section);
    if (sectionIndex < 0) {
      throw new Error("section not found!!!");
    }
    this.sections.splice(sectionIndex, 1);

    // itemSetがどこからも使用されていなくなっていたら削除する
    if (Enumerable.from(this.sections).all(s => s.itemSetId !== itemSetId)) {
      this.removeItemSet(itemSetId);
      for (const remainSection of this.sections) {
        if (remainSection.itemSetId > itemSetId) {
          remainSection.itemSetId = remainSection.itemSetId - 1;
        }
      }
    }

    if (this.currentSectionIndex > 0) {
      this.currentSectionIndex--;
    }
  }

  removeItemSet(id: number): void {
    this.itemSets.splice(id, 1);
    this._idDictionary.splice(id, 1);
    this._nameDictionary.splice(id, 1);
}

  private getSectionIndex(section: Section): number {
    for (let index = 0; index < this.sections.length; ++index) {
      if (this.sections[index] === section) { return index; }
    }
    return -1;
  }

  addItem(added: IItem, targetSectionIndex?: number): void {
    const sectionIndex = (targetSectionIndex === null || targetSectionIndex === undefined)
      ? this.currentSectionIndex
      : targetSectionIndex;
    const section = this.sections[sectionIndex];
    this.pushItemToItemSet(added, section.itemSetId);
    const cloned = section.clone();
    cloned.itemPrivateIds.push(added.privateId);
    this.sections.splice(sectionIndex, 1, cloned);
    this.lastFocusedItem = added;
  }

  pushItemToItemSet(pushed: IItem, itemSetId: number): void {
    const items = this.itemSets[itemSetId].items;
    pushed.privateId = items.length;
    items.push(pushed);
    if (pushed && pushed.id && !this._idDictionary[itemSetId][pushed.id]) {
      this._idDictionary[itemSetId][pushed.id] = pushed.privateId;
    }
    const casted = (pushed as AbstractValuedItem);
    if (casted && casted.name && !this._nameDictionary[itemSetId][casted.name]) {
      this._nameDictionary[itemSetId][casted.name] = pushed.privateId;
    }
  }

  removeItem(removed: IItem, targetSectionIndex?: number): void {
    const sectionIndex = (targetSectionIndex === null || targetSectionIndex === undefined)
      ? this.currentSectionIndex
      : targetSectionIndex;
    if (!!this.lastFocusedItem && (this.lastFocusedItem.privateId === removed.privateId)) {
      this.lastFocusedItem = null;
    }
    const section = this.sections[sectionIndex];
    this.removeItemFromItemSet(removed, section.itemSetId);
    const items = this.itemSets[section.itemSetId].items;
    items.splice(removed.privateId, 1, null);  // 削除するとprivateIdとindexがずれてしまうのでnullと置き換える.
    const cloned = section.clone();
    cloned.itemPrivateIds.splice(cloned.itemPrivateIds.indexOf(removed.privateId), 1);
    this.sections.splice(sectionIndex, 1, cloned);
  }

  removeItemFromItemSet(removed: IItem, itemSetId: number): void {
    const items = this.itemSets[itemSetId].items;
    items.splice(removed.privateId, 1, null);  // 削除するとprivateIdとindexがずれてしまうのでnullと置き換える.
    if (removed && removed.id && this._idDictionary[itemSetId][removed.id]) {
      this._idDictionary[itemSetId][removed.id] = undefined;
    }
    const casted = (removed as AbstractValuedItem);
    if (casted && casted.name && this._nameDictionary[itemSetId][casted.name]) {
      this._nameDictionary[itemSetId][casted.name] = undefined;
    }
  }

  updateItem(updated: IItem, options?: IDocumentItemUpdateOptions): void {
    const isGeometryOnly = !!options && !!options.isGeometryOnly;
    if (!isGeometryOnly) {  // 座標系のみの更新を明示的に指定されてる場合は更新オブジェクト数のカウントは行わない.
      const reflexive = !!options && !!options.reflexive;
      // 循環参照対応のため更新オブジェクト数を管理する
      this.setUpdateObjectItemCount(!reflexive);
    }

    const targetItemSetId = options ? options.itemSetId : undefined;
    const isQuoting = !!options && !!options.isQuoting;
    const itemSetId  = (targetItemSetId === null || targetItemSetId === undefined)
      ? this.itemSetId
      : targetItemSetId;
    const original = this.itemSets[itemSetId].items[updated.privateId];
    const newlyUpdated = this.clearProperty(updated, original, isQuoting);
    this.updateItemToItemSet(newlyUpdated, itemSetId);

    if (isGeometryOnly) {
      return; // 座標系のみの更新を明示的に指定されてる場合は連鎖更新しない.
    }

    this.handleItemUpdated(itemSetId, original, newlyUpdated, isQuoting);

    // もともと文書内転記はAbstractValuedItem内のhandleItemUpdatedの連鎖更新で処理していたが、
    // pageKeyで指定されたページにオブジェクトが見つからない場合の分岐の対応が難しいため、
    // EditingDocumentで処理するようにしている
    if (instanceOfIPasteRequestable(newlyUpdated) && newlyUpdated.isValueUpdated(original)) {
      const pasteExecutor = new PasteExecutor(this, isQuoting);
      pasteExecutor.execute(newlyUpdated, itemSetId);
    }
    if (instanceOfIDateLinkRequestable(newlyUpdated) && newlyUpdated.isValueUpdated(original)) {
      const dateLinkExecutor = new DateLinkExecutor(this);
      dateLinkExecutor.execute(newlyUpdated, itemSetId);
    }

    // 高さ変更による再配置が必要な場合は再配置を行う.
    const slider = new ObjectPositionSlider(this);
    slider.apply(itemSetId, original, newlyUpdated);

    if (instanceOfIRelatedExternalScriptExecutable(newlyUpdated) && newlyUpdated.isValueUpdated(original)) {
      this.externalScriptRunner.addScriptExecuteRequest(itemSetId, newlyUpdated);
    }
    if (!!this.lastFocusedItem && (this.lastFocusedItem.privateId === newlyUpdated.privateId && this.itemSetId === itemSetId)) {
      this.lastFocusedItem = newlyUpdated;
    }

    const sourceApplicator = new SourceApplicator(this);
    sourceApplicator.applicate(original, updated, newlyUpdated, itemSetId);
  }

  private clearProperty(updated: IItem, original: IItem, isQuoting: boolean): IItem {
    const clone = updated.clone();
    if (instanceOfIIsValueUpdatedSpecifiable(clone) && clone.isValueUpdated(original) && instanceOfIQuotable(clone) && !isQuoting) {
      clone.isQuoted = false;
    }
    return clone;
  }

  private _updateObjectItemCount: number;

  private setUpdateObjectItemCount(initialize: boolean): void {
    this._updateObjectItemCount = initialize
      ? 1
      : this._updateObjectItemCount + 1;
    const configuration = store.state.configuration.behavior.document.editor.chainedObjectUpdation.overflow.max;
    if (this._updateObjectItemCount > configuration) {
      this._logger.error("更新オブジェクト数超過");
      const formatNameWording = store.state.configuration.wording.format.name;
      throw new UpdateObjectCountExceedError("1度に更新されるオブジェクト数が" + configuration + "を超えました。循環参照がないか" + formatNameWording + "を確認してください。");
    }
  }

  updateItemToItemSet(updated: IItem, itemSetId: number): void {
    const items = this.itemSets[itemSetId].items;
    items.splice(updated.privateId, 1, Object.freeze(updated));
    // 2018/12/5 12:02に大量オブジェクト更新の高速化用に導入したid/名称辞書の更新実装があったが、以下の理由から無意味で紛らわしいだけなので削除.
    // ・辞書は最初に(constructorで)初期化されていて、更新でidや名称変わるケースはDocuMaker仕様的に現状ありえない.
    // ・しかもバグってそう.(同方向判定が逆...)
    // 将来的に更新時にメンテが必要になる仕様拡張があった場合は改めて実装し直す事.(元々あった実装は同方向判定が間違ってそうなので注意...)
  }

  handleItemUpdated(itemSetId: number, originalItem: IItem, updatedItem: IItem, isQuoting: boolean): void {
    if (itemSetId < 0) { throw new Error("itemSetId is invalid!!!"); }
    const items = this.itemSets[itemSetId].items;
    const sectionItems = this.getUpdatedSectionItems(itemSetId, updatedItem.privateId);
    Enumerable.from(items)
      .where(i => !!i)
      .forEach(i => {
        const updated = this.handleItemUpdatedWhenNeeded(originalItem, updatedItem, items, sectionItems, i, itemSetId);
        if (updated) {
          this.updateItem(updated, {
            itemSetId,
            reflexive: true,
            isQuoting,
          });
        }
      });
  }

  private handleItemUpdatedWhenNeeded(original: IItem, updated: IItem, items: IItem[], updatedSectionItems: IItem[], handler: IItem, itemSetId: number): IItem {
    if (!handler) { return null; }
    const isInSameSection = !!EditingDocument._privateIdFinder.find(handler, updatedSectionItems);
    const itemAccessor = (f?: ObjectId | string | ((i: IItem) => boolean), is?: boolean) => this.findItem(itemSetId, is ? updatedSectionItems : items, f);
    const itemAccessors = (f?: ObjectId | string | ((i: IItem) => boolean), is?: boolean) => this.findItems(itemSetId, is ? updatedSectionItems : items, f);
    return (handler === updated)
      ? handler.handleOwnUpdated(original, itemAccessor, itemAccessors)
      : handler.handleItemUpdated(original, updated, isInSameSection, itemAccessor, itemAccessors);
  }

  private static readonly _privateIdFinder = new BinaryFinder((s: IItem) => s.privateId);

  private getUpdatedSectionItems(itemSetId: number, updatedItemPrivateId: number): IItem[] {
    if (itemSetId < 0) { throw new Error("itemSetId is invalid!!!"); }
    const updatedSectionItemPrivateIds = this.getUpdatedSectionItemPrivateIds(itemSetId, updatedItemPrivateId);
    return Enumerable.from(this.itemSets[itemSetId].items)
    .where(i => !!i && (0 <= updatedSectionItemPrivateIds.indexOf(i.privateId)))
    .toArray();
  }

  private getUpdatedSectionItemPrivateIds(itemSetId: number, updatedItemPrivateId: number): number[] {
    if (itemSetId < 0) { throw new Error("itemSetId is invalid!!!"); }
    const updatedSection = Enumerable.from(this.sections).firstOrDefault(s => (s.itemSetId === itemSetId) && (0 <= s.itemPrivateIds.indexOf(updatedItemPrivateId)));
    return !updatedSection
      ? []
      : updatedSection.itemPrivateIds;
  }

  private findItem(itemSetId: number, items: IItem[], finder?: ObjectId | string | ((i: IItem) => boolean)): IItem {
    if (finder instanceof ObjectId) {
      // ObjectIdの場合はid検索.
      return items[this._idDictionary[itemSetId][finder.value]];
    } else if (typeof finder === "string") {  // instanceof Stringではダメらしい.
      // stringの場合はname検索.
      return items[this._nameDictionary[itemSetId][finder]];
    } else {
      return this.findCurrentItem(items, finder);
    }
  }

  private findCurrentItem(items: IItem[], predicate: (i: IItem) => boolean): IItem {
    return Enumerable.from(items)
      .firstOrDefault(i => i && predicate(i));
  }

  private findItems(itemSetId: number, items: IItem[], finder?: ObjectId | string | ((i: IItem) => boolean)): IItem[] {
    if ((finder instanceof ObjectId) || (typeof finder === "string")) {
      const found = this.findItem(itemSetId, items, finder);
      return found ? [ found ] : [];
    } else {
      return this.findCurrentItems(items, finder);
    }
  }

  private findCurrentItems(items: IItem[], predicate: (i: IItem) => boolean): IItem[] {
    return Enumerable.from(items)
      .where(i => i && predicate(i))
      .toArray();
  }

  private getDrawableItems(sectionIndex: number): IDrawableItem[] {
    if (sectionIndex < 0) { throw new Error("sectionIndex is invalid!!!"); }
    const section = this.sections[sectionIndex];
    const itemSet = this.itemSets[section.itemSetId];
    return Enumerable.from(section.itemPrivateIds)
      .select(i => itemSet.items[i])
      .where(i => instanceOfIDrawableItem(i) && !this.isUnhandledObject(section, i))
      .select(i => i as IDrawableItem)
      .toArray();
  }

  /**
   * オブジェクトがページの外側にあると、フォーマットエディタ上で見えないためメンテナンスできない
   * ページの外側のオブジェクトは文書編集中に表示されず・印刷にも描画されない
   * いくつかのフォーマットではコンテンツチームも把握していないオブジェクトが領域外にある模様・・
   */
  private isUnhandledObject(section: Section, item: IDrawableItem): boolean {
    return !section.isPageSplittable
        && (item.geometry.rectangle.y > section.size.height);
  }

  /**
   * エラー発生時はErrorをthrowしますが、
   * 外部スクリプト失敗時はthrowせず、戻り値がfalseになります。
   */
  async merge(source: Document, forceUpdate: boolean = false, shouldQuoteTemplateId: boolean = false): Promise<boolean> {
    const configuration = store.getters.configuration as IConfiguration;
    const merger = new EditingDocumentMerger(this);
    const isByName = !merger.isSameFormat(source);
    merger.mergeDocument(source, isByName, forceUpdate, shouldQuoteTemplateId);
    await merger.copyImagesFromDocumentAsync();
    await merger.executeScriptInDocumentAtCalculateAsync(configuration.behavior.document.editor.externalScript.shouldExecuteAsStackedRequest);
    return merger.executeStackedScriptsAsyncWithResult();
  }

  get attributes(): DocumentAttributes {
    return this.source.attributes;
  }

  set attributes(source: DocumentAttributes) {
    this.source.attributes = source;
  }

  get extraFormats(): FormatReference[] {
    return this.source.extraFormats;
  }

  get referenceFiles(): ReferenceFileInformation[] {
    return this.source.referenceFiles;
  }

  get hisInformation(): HisInformation {
    return this.source.hisInformation;
  }

  setHisInformation(source: HisInformation, shouldUpdateEmptyObjectOnly: boolean): void {
    const applicator = new HisInformationApplicator(this);
    applicator.applyByUpdate(source, false, shouldUpdateEmptyObjectOnly);
    this.source.hisInformation = source;
    this.source.isNewHisInformationApplied = true;
  }

  get patientProfile(): PatientProfileInformation {
    return this.source.patientProfile;
  }

  set patientProfile(source: PatientProfileInformation) {
    this.source.patientProfile = source;
  }

  get selectedInstitution(): InstitutionItem {
    return this.source.selectedInstitution;
  }

  set selectedInstitution(source: InstitutionItem) {
    this.source.selectedInstitution = source;
  }

  get designatedDoctorInformations(): DesignatedDoctorInformation[] {
    return this.source.designatedDoctorInformations;
  }

  public executeTextPaste(isQuoting: boolean): void {
    const pasteExecutor = new PasteExecutor(this, isQuoting);
    pasteExecutor.executeAll();
  }

  public executeDateLink(): void {
    const dateLinkExecutor = new DateLinkExecutor(this);
    dateLinkExecutor.executeAll();
  }

  get isNaturalTextEnable(): boolean {
    return !!this.naturalText;
  }

  get naturalText(): string {
    return this.naturalTexts.reduce((a, b) => a.concat(b), "");
  }

  get naturalTexts(): string[] {
    return this.naturalTextItems.map(i => this.getNaturalText(i));
  }

  private get naturalTextItems(): IItem[][] {
    const comparisons = [this.compareY, this.compareX];
    return Enumerable.from(this.sections)
      .select(s => {
        const itemSet = this.itemSets[s.itemSetId];
        const naturalTextItemsPerPage = Enumerable.from(s.itemPrivateIds)
          .select(i => itemSet.items[i])
          .where(i => instanceOfINaturalTextSpecifiable(i) && i.isNaturalViewEnable)
          .toArray();
        return naturalTextItemsPerPage.sort((a, b) => {
          for (const compare of comparisons) {
            if (!instanceOfIDrawable(a) || !instanceOfIDrawable(b)) { continue; }
            return compare()(a, b);
          }
        });
      })
      .toArray();
  }

  private getNaturalText(items: IItem[]): string {
    return items.map(i => instanceOfINaturalTextSpecifiable(i) ? i.currentNaturalText : "").reduce((a, b) => a.concat(b), "");
  }

  private compareY(): (a: IDrawable, b: IDrawable) => number {
    return (a, b) => Comparator.compare(a.geometry.rectangle.y, b.geometry.rectangle.y);
  }

  private compareX(): (a: IDrawable, b: IDrawable) => number {
    return (a, b) => Comparator.compare(a.geometry.rectangle.x, b.geometry.rectangle.x);
  }

  clone(source?: Document, restrictions?: IRestrictions): EditingDocument {
    const clone = new EditingDocument(
      source ? source : this.source,
      restrictions ? restrictions : this.restrictions,
      this.currentSectionIndex,
      this._lastFocusedItem,
      this._lastFocusedObject,
      this.scale,
      this.shouldDrawEditStyle,
      this.externalScriptRunner,
      this.shouldInputFreeAll,
      this.alerts,
      this.shouldMarkAlerts,
      this.shouldDrawTransparentAll,
      this.shouldMarkQuote,
    );
    clone._isAlreadyInitialized = this._isAlreadyInitialized;
    clone._initializer = this._initializer;
    return clone;
  }

  private readonly _logger = LoggerFactory.getLogger();
}
